import { Button } from '@mui/material';
import { useNotification } from '@revenuewell/front-end-bundle';
import moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { ContactApi } from '../../../../../../services/api-client/contact-api';
import { PatientNote } from '../../../../../../services/api-client/contact-api.types';
import { NoDataComponent } from '../../../../../shared/grid/no-data';
import { AddNoteDialog } from '../../../../../shared/notes/add-note/add-note';
import { PatientProfileHeader } from '../../header/header';
import { QuickNavMenu } from '../../header/quick-nav-menu/quick-nav-menu';
import { CompleteStatus, DotSeparatedTitle, LiSeparator, List, ListItem } from '../../patient-profile-widget.styles';
import { ViewContent } from '../shared/view-content/view-content';
import { NotesSkeleton } from './notes.skeleton';
import { NoteInfo, NoteText, NotesContent, Toolbar } from './notes.styles';

export const NotesView: FC = () => {
  const { showError } = useNotification();
  const { contactDetails, locationId } = usePatientProfile();
  const [notes, setNotes] = useState<PatientNote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedNote, setSelectedNote] = useState<PatientNote>();
  const [noteDialogOpened, setNoteDialogOpened] = useState(false);
  const contactApi = useMemo(() => ContactApi.getInstance(), [ContactApi]);

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = useCallback(async () => {
    setIsLoading(true);

    try {
      const notes = await contactApi.getNotes(contactDetails.id, locationId);

      setNotes(notes);
    } catch {
      showError('Unable to load notes.');
    } finally {
      setIsLoading(false);
    }
  }, [contactDetails, locationId]);

  const closeDialog = useCallback(() => {
    setNoteDialogOpened(false);
    setSelectedNote(undefined);
  }, []);

  const handleNoteClick = useCallback((note: PatientNote) => {
    setSelectedNote(note);
    setNoteDialogOpened(true);
  }, []);

  const handleAddNoteClick = useCallback(() => {
    setNoteDialogOpened(true);
  }, []);

  const handleNoteUpdate = useCallback(
    (note: PatientNote) => {
      const updatedNotes = notes.map((n) => (n.id === note.id ? note : n));

      setNotes(updatedNotes);
      closeDialog();
    },
    [notes, closeDialog]
  );

  const handleNoteAdd = useCallback(() => {
    loadNotes();
    closeDialog();
  }, [loadNotes]);

  return (
    <>
      <PatientProfileHeader />
      <QuickNavMenu />
      <ViewContent>
        <NotesContent>
          {isLoading && <NotesSkeleton />}
          {!isLoading && notes.length === 0 && <NoDataComponent />}
          {!isLoading && notes.length > 0 && (
            <Scrollbars autoHide style={{ flexGrow: 1 }}>
              <List>
                {notes.map((note, index) => (
                  <>
                    <ListItem key={note.id} onClick={() => handleNoteClick(note)}>
                      <NoteInfo>
                        <DotSeparatedTitle
                          leftTitle={note.date ? moment(note.date).format('MM/DD/YYYY') : null}
                          rightTitle={
                            note.authorFullName ? <CompleteStatus>{note.authorFullName}</CompleteStatus> : null
                          }
                        />
                      </NoteInfo>
                      <NoteText>{note.note}</NoteText>
                    </ListItem>
                    {index !== notes.length - 1 && <LiSeparator key={1000 + index} />}
                  </>
                ))}
              </List>
            </Scrollbars>
          )}
          {!isLoading && (
            <Toolbar>
              <Button onClick={handleAddNoteClick} variant="contained" disableElevation fullWidth>
                Add Note
              </Button>
            </Toolbar>
          )}
        </NotesContent>
      </ViewContent>
      <AddNoteDialog
        note={selectedNote}
        opened={noteDialogOpened}
        onClose={closeDialog}
        onAddNode={handleNoteAdd}
        onUpdateNote={handleNoteUpdate}
      />
    </>
  );
};
